// src/pages/Dashboard.jsx
// MD Dashboard — Role 1 and above
// Shows: today's collection, TPA aging buckets, top expenses, cash tally status
// All data from GET /entities/:id/dashboard

import { useState, useEffect } from "react";
import { api } from "../services/api";

export default function Dashboard({ entity }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!entity?._id) return;
    load();
    const interval = setInterval(load, 5 * 60 * 1000); // refresh every 5 mins
    return () => clearInterval(interval);
  }, [entity?._id]);

  async function load() {
    try {
      const res = await api.get(`/entities/${entity._id}/dashboard`);
      setData(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <Loader />;
  if (error)   return <ErrorBox msg={error} onRetry={load} />;
  if (!data)   return null;

  const tpa = data.tpaOutstanding || {};

  return (
    <div style={S.page}>

      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>{entity.name}</h1>
          <div style={S.subtitle}>
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
        <button onClick={load} style={S.refreshBtn}>↻ Refresh</button>
      </div>

      {/* Top stats */}
      <div style={S.statsRow}>
        <StatCard
          label="Month-to-date collection"
          value={"₹" + lakh(data.mtdCollection)}
          sub="All payment modes"
          color="#1B3F6E"
          bg="#EBF5FB"
        />
        <StatCard
          label="Month-to-date expenses"
          value={"₹" + lakh(data.mtdExpenses)}
          sub="All categories"
          color="#C85A00"
          bg="#FFF3E0"
        />
        <StatCard
          label="Net position"
          value={"₹" + lakh(data.mtdCollection - data.mtdExpenses)}
          sub="Collection minus expenses"
          color={data.mtdCollection > data.mtdExpenses ? "#1A7A4A" : "#B71C1C"}
          bg={data.mtdCollection > data.mtdExpenses ? "#E8F5EE" : "#FFEBEE"}
        />
        <StatCard
          label="Insurance outstanding"
          value={"₹" + lakh(tpa.total || 0)}
          sub="All TPA / insurers"
          color="#7B4F00"
          bg="#FFF8E1"
        />
      </div>

      {/* Middle row */}
      <div style={S.twoCol}>

        {/* TPA Aging */}
        <div style={S.card}>
          <div style={S.cardHeader}>Insurance outstanding by age</div>
          {[
            { label: "0–30 days",  key: "0-30",  color: "#1A7A4A", bg: "#E8F5EE" },
            { label: "31–60 days", key: "31-60", color: "#C85A00", bg: "#FFF3E0" },
            { label: "61–90 days", key: "61-90", color: "#B71C1C", bg: "#FFEBEE" },
            { label: "90+ days",   key: "90+",   color: "#7B0000", bg: "#FCE4EC" },
          ].map(({ label, key, color, bg }) => (
            <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 14px", marginBottom: 6, background: bg, borderRadius: 8 }}>
              <div style={{ fontSize: 13, color: "#455A64" }}>{label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color }}>
                ₹{lakh(tpa[key] || 0)}
              </div>
            </div>
          ))}
          {(!tpa.total || tpa.total === 0) && (
            <div style={S.empty}>No outstanding TPA dues</div>
          )}
          <div style={{ paddingTop: 8, borderTop: "1px solid #F0F4F8", display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "#607D8B" }}>Total outstanding</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1B3F6E" }}>₹{lakh(tpa.total || 0)}</span>
          </div>
        </div>

        {/* Daily collection chart (last 7 days) */}
        <div style={S.card}>
          <div style={S.cardHeader}>Daily collection — last 7 days</div>
          <MiniBarChart data={data.revenueByDay || []} />
          {(!data.revenueByDay || data.revenueByDay.length === 0) && (
            <div style={S.empty}>No billing data uploaded yet this month</div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div style={S.twoCol}>

        {/* Pending entries */}
        <div style={S.card}>
          <div style={S.cardHeader}>Entries needing attention</div>
          {data.pendingEntries > 0 ? (
            <div style={{ padding: "14px 16px", background: "#FFF3E0", borderRadius: 8,
              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, color: "#C85A00", fontSize: 14 }}>{data.pendingEntries} entries pending</div>
                <div style={{ fontSize: 12, color: "#7B4F00", marginTop: 2 }}>Awaiting review or approval</div>
              </div>
              <a href="/entries?status=draft" style={{ fontSize: 12, color: "#C85A00", textDecoration: "none", fontWeight: 600 }}>View →</a>
            </div>
          ) : (
            <div style={{ ...S.empty, color: "#1A7A4A" }}>✓ All entries are up to date</div>
          )}
        </div>

        {/* Cash tally status */}
        <div style={S.card}>
          <div style={S.cardHeader}>Today's cash tally</div>
          {data.cashTallyToday ? (
            <div style={{
              padding: "14px 16px", borderRadius: 8,
              background: data.cashTallyToday.status === "tallied" ? "#E8F5EE" : "#FFF3E0",
            }}>
              <div style={{ fontWeight: 600, fontSize: 14,
                color: data.cashTallyToday.status === "tallied" ? "#1A7A4A" : "#C85A00" }}>
                {data.cashTallyToday.status === "tallied" ? "✓ Cash tallied" : "⚠ Variance noted"}
              </div>
              {data.cashTallyToday.variance !== 0 && (
                <div style={{ fontSize: 12, color: "#607D8B", marginTop: 4 }}>
                  Variance: ₹{Math.abs(data.cashTallyToday.variance).toLocaleString("en-IN")}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: "14px 16px", background: "#FFEBEE", borderRadius: 8 }}>
              <div style={{ fontWeight: 600, color: "#B71C1C", fontSize: 14 }}>Not submitted yet</div>
              <div style={{ fontSize: 12, color: "#7B0000", marginTop: 4 }}>
                <a href="/tally" style={{ color: "#B71C1C" }}>Submit today's cash tally →</a>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

// ── Mini bar chart ────────────────────────────────────────────────────────────
function MiniBarChart({ data }) {
  if (!data.length) return null;
  const last7  = data.slice(-7);
  const maxVal = Math.max(...last7.map(d => d.amount), 1);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 100, padding: "0 4px" }}>
      {last7.map((d, i) => {
        const pct = (d.amount / maxVal) * 100;
        const dt  = new Date(d.date);
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{ fontSize: 10, color: "#90A4AE" }}>₹{shortNum(d.amount)}</div>
            <div style={{ width: "100%", height: pct + "%", minHeight: 4,
              background: "#2E86AB", borderRadius: "4px 4px 0 0", transition: "height .3s" }} />
            <div style={{ fontSize: 10, color: "#90A4AE" }}>
              {dt.toLocaleDateString("en-IN", { day: "2-digit" })}/{dt.getMonth() + 1}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function lakh(n) {
  const num = n || 0;
  if (num >= 100000) return (num / 100000).toFixed(2) + "L";
  if (num >= 1000)   return (num / 1000).toFixed(1) + "K";
  return num.toFixed(0);
}
function shortNum(n) {
  if (n >= 100000) return (n/100000).toFixed(1) + "L";
  if (n >= 1000)   return (n/1000).toFixed(0)   + "K";
  return n.toFixed(0);
}

function StatCard({ label, value, sub, color, bg }) {
  return (
    <div style={{ background: bg, borderRadius: 12, padding: "18px 20px", flex: 1 }}>
      <div style={{ fontSize: 11, color: "#607D8B", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#90A4AE" }}>{sub}</div>
    </div>
  );
}

function Loader() {
  return <div style={{ padding: 40, textAlign: "center", color: "#90A4AE", fontSize: 14 }}>Loading dashboard…</div>;
}

function ErrorBox({ msg, onRetry }) {
  return (
    <div style={{ padding: 24, background: "#FFEBEE", borderRadius: 12, textAlign: "center" }}>
      <div style={{ color: "#B71C1C", marginBottom: 12, fontSize: 14 }}>{msg}</div>
      <button onClick={onRetry} style={{ padding: "8px 20px", background: "#1B3F6E", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>Retry</button>
    </div>
  );
}

const S = {
  page:       { maxWidth: 1100, margin: "0 auto" },
  header:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  h1:         { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: "0 0 4px" },
  subtitle:   { fontSize: 13, color: "#90A4AE" },
  refreshBtn: { padding: "8px 16px", background: "#fff", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, cursor: "pointer", color: "#455A64" },
  statsRow:   { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 16 },
  twoCol:     { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 },
  card:       { background: "#fff", borderRadius: 12, padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,.07)" },
  cardHeader: { fontSize: 12, fontWeight: 600, color: "#455A64", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 14 },
  empty:      { fontSize: 13, color: "#90A4AE", textAlign: "center", padding: "20px 0" },
};
